import d3 from 'd3';
import { useRef } from 'react';
import ReactFauxDOM from 'react-faux-dom';

import Row from 'components/UIKit/Flex/Row';
import {
  BLACK, GREEN, ORANGE, SECONDARY_RED,
} from 'style/colors';
import { ErrorIcon, ValidIcon, WarningIcon } from 'components/icons';

type Props = {
  criticalColor: string,
  criticalData: number,
  isFetching: boolean,
  type: string,
  validColor: string,
  warningColor: string,
  warningData: number
}

type renderSVGProps = {
  abbr: string,
  criticalColor: string,
  criticalCount: number,
  isFetching: boolean,
  type: string,
  validColor: string,
  warningColor: string,
  warningCount: number
}

const Donut = (props : Props) => {
  const renderSVG = ({
    criticalColor,
    criticalCount,
    isFetching,
    params,
    path,
    type,
    validColor,
    warningColor,
    warningCount,
    elRef
  }: renderSVGProps) => {
    const pathColor = (criticalCount > 0 && criticalColor)
      || (warningCount > 0 && warningColor)
      || validColor;
    const node: any = ReactFauxDOM.createElement('svg');

    const diameter: number = elRef?.current?.offsetWidth || 0;
    const arcOffset = diameter > 0 ? 4 : 0;
    const radius: number = (diameter / 2) - (arcOffset);
    const innerPadding = 18;
    const numCharOffset = 1.5;
    const iconOffset = 2;
    const baseCharOffset = 4;

    const svg = d3.select(node).attr({
      width: diameter,
      height: diameter,
    }).append('g').attr({
      transform: `translate(${radius + arcOffset},${radius + arcOffset})`,
    });

    const criticalNumLength = `${criticalCount}`.length;
    const warningNumLength = `${warningCount}`.length;

    const getIconX = (numLength) => {
      const diff = 3 - numLength;
      return -(innerPadding + (-diff) + (numLength * iconOffset));
    };

    const getNumX = (numLength) => {
      const diff = 3 - numLength;
      return baseCharOffset + diff + numLength + numCharOffset;
    };

    const getIcon = (count, icon) => (count
      ? icon({ height: 13, width: 13 }) : ValidIcon({ height: 13, width: 13 }));

    if (!isFetching) {
      svg.append('text')
        .attr({ x: getNumX(criticalNumLength), y: -30 })
        .style({
          fill: criticalCount ? criticalColor : BLACK,
          'font-size': '1rem',
          'text-anchor': 'middle',
          'font-weight': 'bold',
        })
        .text(`${criticalCount}`);

      svg.append('g')
        .attr({
          transform: `translate(${getIconX(criticalNumLength)}, -41)`,
        })
        .html(getIcon(criticalCount, ErrorIcon));

      svg.append('text')
        .attr('x', 0)
        .attr('y', -10)
        .style({
          fill: BLACK,
          'font-size': '0.8rem',
          'text-anchor': 'middle',
        })
        .text(`Critical ${type}`);

      svg.append('text')
        .attr({ x: getNumX(warningNumLength), y: 25 })
        .style({
          fill: warningCount ? warningColor : BLACK,
          'font-size': '1rem',
          'text-anchor': 'middle',
          'font-weight': 'bold',
        })
        .text(`${warningCount}`);

      svg.append('g')
        .attr({
          transform: `translate(${getIconX(warningNumLength)},14)`,
        })
        .html(getIcon(warningCount, WarningIcon));

      svg.append('text')
        .attr('x', 0)
        .attr('y', 45)
        .style({
          fill: BLACK,
          'font-size': '0.8rem',
          'text-anchor': 'middle',
        })
        .text('Warnings');


      svg.append('line')
        .attr('x1', -30)
        .attr('y1', 5)
        .attr('x2', 30)
        .attr('y2', 5)
        .attr('stroke-width', 2)
        .attr('stroke', '#ccc');
    }

    svg.append('circle')
      .attr({
        r: radius ? radius - innerPadding : 0,
        cx: 0,
        cy: 0,
        stroke: pathColor,
        'stroke-width': 10,
        fill: 'transparent',
      });

    svg.append('a')
      .classed('qcLink', true)
      .attr('xlink:href', [params.program, params.project, path].filter(param => param && param.length).join('/'))
      .append('rect')
      .attr('x', -(radius / 2))
      .attr('y', -(radius / 2))
      .attr('height', radius)
      .attr('width', radius)
      .attr('fill', 'transparent')
      .append('text')
      .attr({
        width: 0,
        height: 0,
        overflow: 'hidden',
      })
      .text('Go to QC Report');

    return node.toReact();
  }

  const elRef = useRef();

    const {
      criticalColor = SECONDARY_RED,
      criticalData,
      isFetching,
      params,
      path,
      type,
      validColor = GREEN,
      warningColor = ORANGE,
      warningData,
    } = props;

    return (
      <div ref={elRef}>
        <Row>
          {renderSVG({
            criticalColor,
            criticalCount: criticalData,
            isFetching,
            params,
            path,
            type,
            validColor,
            warningColor,
            warningCount: warningData,
            elRef,
          })}
        </Row>
      </div>
    );
}

export default Donut;
